import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Calendar } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import ImagePlaceholder from "@/components/site/ImagePlaceholder";

const allProjects = [
    {
        title: "Wohnhaus am Hang",
        location: "Zürich",
        year: "2024",
        tag: "Neubau",
        scope: "Maurerarbeiten · Sichtbeton · Fassade",
        desc: "Ein modernes Einfamilienhaus in Hanglage mit exponierten Betonelementen und feinem Verputz.",
        img: "https://images.unsplash.com/photo-1778084025711-64d04aea3cfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwY29uY3JldGUlMjBhcmNoaXRlY3R1cmUlMjBzd2l0emVybGFuZHxlbnwwfHx8fDE3ODM2OTMwNTV8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Fassadensanierung",
        location: "Basel",
        year: "2024",
        tag: "Sanierung",
        scope: "Verputzarbeiten · Wärmedämmung",
        desc: "Vollflächige Sanierung einer bestehenden Wohnhausfassade mit hochwertigem Feinverputz.",
        img: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBjb25zdHJ1Y3Rpb24lMjByZW5vdmF0aW9ufGVufDB8fHx8MTc4MzY5MzA1NXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Umbau Altbau",
        location: "Bern",
        year: "2023",
        tag: "Umbau",
        scope: "Mauerwerk · Statik · Grundriss",
        desc: "Offene Grundrisse und moderne Räume in einem historischen Berner Altbau.",
        img: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBjb25zdHJ1Y3Rpb24lMjByZW5vdmF0aW9ufGVufDB8fHx8MTc4MzY5MzA1NXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Sichtbeton Villa",
        location: "Luzern",
        year: "2023",
        tag: "Neubau",
        scope: "Betonarbeiten · Schalung · Bewehrung",
        desc: "Eine private Villa in Sichtbetonbauweise – jede Fläche wurde vor Ort geplant und geschalt.",
        img: "https://images.unsplash.com/photo-1573652636601-d6fdcfc59640?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzd2lzcyUyMGFyY2hpdGVjdHVyZSUyMGJ1aWxkaW5nJTIwZmluaXNoZWR8ZW58MHx8fHwxNzgzNjkzMDU1fDA&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Kellerabdichtung",
        location: "Zug",
        year: "2023",
        tag: "Sanierung",
        scope: "Betonarbeiten · Abdichtung",
        desc: "Fachgerechte Sanierung feuchter Kellerwände inklusive Aussenabdichtung.",
        img: "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBjb25zdHJ1Y3Rpb24lMjByZW5vdmF0aW9ufGVufDB8fHx8MTc4MzY5MzA1NXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Anbau Wohnhaus",
        location: "St. Gallen",
        year: "2022",
        tag: "Umbau",
        scope: "Mauerwerk · Verputzarbeiten",
        desc: "Zweigeschossiger Anbau, sauber an die bestehende Substanz angeschlossen.",
        img: "https://images.unsplash.com/photo-1659962911037-bc6bd6d4e813?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzd2lzcyUyMGFyY2hpdGVjdHVyZSUyMGJ1aWxkaW5nJTIwZmluaXNoZWR8ZW58MHx8fHwxNzgzNjkzMDU1fDA&ixlib=rb-4.1.0&q=85",
    },
];

function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in-view"); }),
            { threshold: 0.1 }
        );
        el.querySelectorAll(".fade-up").forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, []);
    return ref;
}

export default function ReferencesPage() {
    const ref = useReveal();

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    return (
        <>
            <Navigation />

            {/* Page hero */}
            <section
                data-testid="referenzen-page-hero"
                className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 bg-[#FEFEFE]"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <Link
                        to="/"
                        data-testid="referenzen-back-link"
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#14100D]/60 hover:text-[#E20514] transition-colors"
                    >
                        <ArrowLeft size={14} /> Zurück zur Startseite
                    </Link>

                    <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                        <div className="lg:col-span-8">
                            <div className="eyebrow mb-6">Referenzen</div>
                            <h1
                                className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tighter text-[#14100D] leading-[0.95]"
                                style={{ fontFamily: "Outfit, sans-serif" }}
                            >
                                Ausgewählte
                                <br />
                                <span className="font-medium">Bauwerke.</span>
                            </h1>
                        </div>
                        <div className="lg:col-span-4">
                            <p className="text-[15px] lg:text-[17px] leading-relaxed text-[#14100D]/70 max-w-md">
                                Ein detaillierter Einblick in unsere abgeschlossenen Arbeiten. Jedes Projekt spiegelt unseren Anspruch an Präzision, Beständigkeit und ruhiges Handwerk wider.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Projects list */}
            <section
                ref={ref}
                data-testid="referenzen-list"
                className="relative bg-[#FEFEFE] pb-28 lg:pb-40"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="border-t border-[rgba(20,16,13,0.1)]">
                        {allProjects.map((p, i) => (
                            <ProjectRow key={p.title} project={p} index={i} />
                        ))}
                    </div>

                    <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-[rgba(20,16,13,0.1)] pt-10">
                        <div className="max-w-lg">
                            <div className="eyebrow mb-3">Ihr Projekt</div>
                            <h3
                                className="text-2xl lg:text-3xl font-medium text-[#14100D]"
                                style={{ fontFamily: "Outfit, sans-serif" }}
                            >
                                Bereit für die nächste Referenz?
                            </h3>
                        </div>
                        <Link
                            to="/kontakt"
                            data-testid="referenzen-cta-contact"
                            className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold"
                            style={{ borderRadius: 2 }}
                        >
                            Projekt anfragen <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
}

function ProjectRow({ project, index }) {
    const isReversed = index % 2 === 1;
    return (
        <article
            data-testid={`referenzen-project-${index}`}
            className={`fade-up grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-16 lg:py-24 border-b border-[rgba(20,16,13,0.1)] items-center`}
        >
            <div className={`lg:col-span-7 ${isReversed ? "lg:order-2" : ""}`}>
                <div className="ref-image aspect-[16/10] group">
                    <ImagePlaceholder />
                </div>
            </div>

            <div className={`lg:col-span-5 ${isReversed ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
                <div className="flex items-center gap-3 text-[10px] tracking-[0.28em] uppercase text-[#E20514] mb-4">
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <span className="h-px w-6 bg-[#E20514]" />
                    <span>{project.tag}</span>
                </div>
                <h2
                    className="text-3xl lg:text-4xl font-medium text-[#14100D] leading-tight"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                >
                    {project.title}
                </h2>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-[13px] text-[#14100D]/70">
                    <span className="inline-flex items-center gap-2">
                        <MapPin size={14} className="text-[#E20514]" /> {project.location}
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Calendar size={14} className="text-[#E20514]" /> {project.year}
                    </span>
                </div>
                <p className="mt-6 text-[15px] lg:text-[16px] leading-relaxed text-[#14100D]/75 max-w-md">
                    {project.desc}
                </p>
                <div className="mt-6 pt-5 border-t border-[rgba(20,16,13,0.1)] max-w-md">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#14100D]/50 mb-2">
                        Leistungen
                    </div>
                    <div className="text-[14px] text-[#14100D]/80">{project.scope}</div>
                </div>
            </div>
        </article>
    );
}
